import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  Checkbox,
  Modal,
  Tabs,
  Select,
  message,
  Spin,
  Dropdown,
} from "antd";
import {
  MailOutlined,
  LockOutlined,
  UserOutlined,
  PhoneOutlined,
  ShopOutlined,
  GlobalOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import "./Register.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { get, post } from "../utils/apiUtils";
import { PASSWORD_REQUIREMENTS, PASSWORD_RULES } from "../utils/passwordRules";
import { useTranslation } from "react-i18next";
import KapInsigntsLogo from "../assets/img/kapininsightsLogo.png";

const { TabPane } = Tabs;
const { Option } = Select;

const Register: React.FC = () => {
  const { t, i18n } = useTranslation("auth");
  const { t: tCommon } = useTranslation("common");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isCodeModalVisible, setIsCodeModalVisible] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [activeTabKey, setActiveTabKey] = useState("1");
  const [countries, setCountries] = useState<any[]>([]);
  const [organizationTypes, setOrganizationTypes] = useState<any[]>([]);
  const [form] = Form.useForm();

  // Language selector handler
  const handleLanguageChange = (e: { key: string }) => {
    i18n.changeLanguage(e.key);
    localStorage.setItem("i18nextLng", e.key);
  };

  // Get current language for menu selection
  const currentLanguage = i18n.language.split("-")[0];
  const languageMenuItems = {
    items: [
      { key: "en", label: tCommon("languages.en") },
      { key: "ar", label: tCommon("languages.ar") },
    ],
    selectedKeys: [currentLanguage],
    onClick: handleLanguageChange,
  };

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await get<any>("countries/");
        if (response.type === "success") {
          setCountries(response.data);
        } else {
          const apiErrorResponse = response.data?.response?.data;
          toast.error(
            apiErrorResponse?.detail ||
              apiErrorResponse?.message ||
              t("register.loadCountriesFailed")
          );
        }
      } catch (error) {
        console.error("Failed to fetch countries:", error);
        toast.error(t("register.loadCountriesFailed"));
      }
    };

    const fetchOrganizationType = async () => {
      try {
        const response = await get<any>("organization_types/");
        if (response.type === "success") {
          setOrganizationTypes(response.data);
        } else {
          const apiErrorResponse = response.data?.response?.data;
          toast.error(
            apiErrorResponse?.detail ||
              apiErrorResponse?.message ||
              t("register.loadOrgTypesFailed")
          );
        }
      } catch (error) {
        console.error("Failed to fetch organization types:", error);
        toast.error(t("register.loadOrgTypesFailed"));
      }
    };
    fetchCountries();
    fetchOrganizationType();
  }, []);

  const showModal = (key: any) => {
    setActiveTabKey(key);
    setIsModalVisible(true);
  };
  const showCodeModal = () => setIsCodeModalVisible(true);
  const changeTab = (key: any) => setActiveTabKey(key);
  const handleOk = () => setIsModalVisible(false);
  const handleCancel = () => setIsModalVisible(false);
  const handleCodeModalCancel = () => setIsCodeModalVisible(false);

  const handleCodeModalOk = async () => {
    setIsCodeModalVisible(false);
    try {
      const response = await post<any>(
        `Users/verify-email/?email=${userEmail}&code=${verificationCode}`,
        {},
        { headers: { "Content-Type": "application/json" } }
      );
      if (response.type === "success") {
        message.success(response.data?.message);
        navigate("/");
      } else {
        throw new Error(`HTTP error! status: ${response.type}`);
      }
    } catch (error: any) {
      toast.error(t("register.verifyFailed"));
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values: any) => {
    setLoading(true);

    const payload = {
      username: values.fullname,
      email: values.email,
      password: values.password,
      password_confirmation: values.confirm,
      phone_number: values.phone,
      organization_name: values.organization_name,
      typeid: values.organizationTypes,
      countryid: values.country,
      agree_to_terms: values.remember,
      opt_info: values.optInfo,
    };

    try {
      const response = await post<any>("Users/register/", payload);
      if (response.type === "success") {
        message.success(t("register.verificationSent"));
        setUserEmail(response.data.email);
        showCodeModal();
      } else {
        const apiErrorResponse = response.data?.response?.data;
        toast.error(
          apiErrorResponse?.detail ||
            apiErrorResponse?.message ||
            t("register.registrationFailed")
        );
      }
    } catch (error: any) {
      console.log("Registration error:", error);
      toast.error(error?.response?.data?.detail || t("register.registrationFailed"));
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  // Password requirement items for display
  const passwordRequirementsList = PASSWORD_REQUIREMENTS.map((req, idx) => ({
    id: idx,
    text: req,
  }));

  return (
    <section className="sign-up">
      <div className="image-icon-register"></div>

      <div className="sign-up-form-container">
        <div className="signup-header">
          <img
            src={KapInsigntsLogo}
            alt="kapinsights"
            className="kapinsights-logo"
          />
          <Dropdown menu={languageMenuItems} trigger={["click"]}>
            <button
              className="signup-language-btn"
              title={tCommon("language")}
              aria-label={tCommon("language")}
            >
              <GlobalOutlined />
              <span>{currentLanguage.toUpperCase()}</span>
            </button>
          </Dropdown>
        </div>

        <Form
          form={form}
          name="register"
          layout="vertical"
          className="sign-up-form"
          initialValues={{ remember: false, optInfo: false }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
        >
          {/* HEADER */}
          <h3 className="signup-header-text">{t("register.title")}</h3>
          <p className="signup-login-link">
            {t("register.haveAccount")}{" "}
            <a href="/">{t("register.loginHere")}</a>
          </p>

          {/* LOGIN DETAILS SECTION */}
          <label className="section-label">{t("register.loginDetails")}</label>

          <div className="form-row">
            <Form.Item
              label={t("register.email")}
              name="email"
              rules={[
                { required: true, message: t("register.emailRequired") },
                { type: "email", message: t("register.emailInvalid") },
              ]}
            >
              <Input
                placeholder={t("register.emailPlaceholder")}
                className="signupEmailInput"
                prefix={<MailOutlined />}
              />
            </Form.Item>

            <Form.Item
              label={t("register.password")}
              name="password"
              rules={PASSWORD_RULES}
              hasFeedback
            >
              <Input.Password
                placeholder={t("register.passwordPlaceholder")}
                prefix={<LockOutlined />}
              />
            </Form.Item>
          </div>

          {/* PASSWORD REQUIREMENTS */}
          <div className="password-requirements-box">
            {passwordRequirementsList.map((req) => (
              <div key={req.id} className="requirement">
                <div className="requirement-icon">
                  <CheckCircleOutlined style={{ color: "#5b21b6" }} />
                </div>
                <div className="requirement-text">{req.text}</div>
              </div>
            ))}
          </div>

          {/* CONFIRM PASSWORD */}
          <Form.Item
            label={t("register.confirmPassword")}
            name="confirm"
            dependencies={["password"]}
            hasFeedback
            rules={[
              { required: true, message: t("register.confirmPasswordRequired") },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(t("register.passwordMismatch"))
                  );
                },
              }),
            ]}
          >
            <Input.Password
              placeholder={t("register.confirmPasswordPlaceholder")}
              prefix={<LockOutlined />}
            />
          </Form.Item>

          {/* USER INFORMATION SECTION */}
          <label className="section-label">{t("register.userInformation")}</label>

          <div className="form-row">
            <Form.Item
              label={t("register.fullName")}
              name="fullname"
              rules={[
                { required: true, message: t("register.fullNameRequired") },
              ]}
            >
              <Input
                placeholder={t("register.fullNamePlaceholder")}
                className="signupEmailInput"
                prefix={<UserOutlined />}
              />
            </Form.Item>

            <Form.Item
              label={t("register.phoneNumber")}
              name="phone"
              rules={[
                { required: true, message: t("register.phoneNumberRequired") },
              ]}
            >
              <Input
                placeholder={t("register.phoneNumberPlaceholder")}
                className="signupEmailInput"
                prefix={<PhoneOutlined />}
              />
            </Form.Item>
          </div>

          {/* ORGANIZATION DETAILS SECTION */}
          <label className="section-label">{t("register.organizationDetails")}</label>

          <div className="form-row-three">
            <Form.Item
              label={t("register.organizationName")}
              name="organization_name"
              rules={[
                {
                  required: true,
                  message: t("register.organizationNameRequired"),
                },
              ]}
            >
              <Input
                placeholder={t("register.organizationNamePlaceholder")}
                className="signupEmailInput"
                prefix={<ShopOutlined />}
              />
            </Form.Item>

            <Form.Item
              label={t("register.organizationType")}
              name="organizationTypes"
              rules={[
                {
                  required: true,
                  message: t("register.organizationTypeRequired"),
                },
              ]}
            >
              <Select placeholder={t("register.organizationTypePlaceholder")}>
                {organizationTypes.map((orgType: any) => (
                  <Option key={orgType.id} value={orgType.code}>
                    {orgType.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={t("register.country")}
              name="country"
              rules={[{ required: true, message: t("register.countryRequired") }]}
            >
              <Select
                allowClear
                showSearch
                placeholder={t("register.countryPlaceholder")}
                filterOption={(input: any, option: any) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {countries.map((country: any) => (
                  <Option key={country.id} value={country.id}>
                    {country.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          {/* CHECKBOXES */}
          <div className="checkbox-group">
            {/* REQUIRED */}
            <Form.Item
              name="remember"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(t("register.agreeTermsRequired"))
                        ),
                },
              ]}
            >
              <Checkbox>
                {t("register.agreeTerms")}{" "}
                <a onClick={() => showModal("1")}>
                  {t("register.termsOfService")}{" "}
                </a>
                <a onClick={() => showModal("2")}>
                  {t("register.privacyPolicy")}
                </a>
                <a onClick={() => showModal("3")}>
                  {t("register.cookiePolicy")}
                </a>
              </Checkbox>
            </Form.Item>

            {/* OPTIONAL */}
            <Form.Item
              name="optInfo"
              valuePropName="checked"
            >
              <Checkbox>{t("register.marketingOptIn")}</Checkbox>
            </Form.Item>
          </div>

          {/* SUBMIT BUTTON */}
          <Form.Item>
            <Button htmlType="submit" className="signup-continue-button">
              {t("register.continueButton")} {loading && <Spin size="small" />}
            </Button>
          </Form.Item>
        </Form>

        {/* TERMS/PRIVACY/COOKIE MODAL */}
        <Modal
          title=""
          open={isModalVisible}
          onOk={handleOk}
          onCancel={handleCancel}
          footer={[
            <Button
              key="submit"
              onClick={handleOk}
              className="gotIt-button"
            >
              {t("register.gotIt")}
            </Button>,
          ]}
          wrapClassName="terms-of-service-modal"
        >
          <Tabs
            defaultActiveKey="1"
            activeKey={activeTabKey}
            onChange={changeTab}
          >
            <TabPane tab={t("register.termsTab")} key="1">
              <div className="scrollable-content">
                {/* Terms content */}
              </div>
            </TabPane>
            <TabPane tab={t("register.privacyTab")} key="2">
              <div className="scrollable-content">
                {/* Privacy content */}
              </div>
            </TabPane>
            <TabPane tab={t("register.cookieTab")} key="3">
              <div className="scrollable-content">
                {/* Cookie content */}
              </div>
            </TabPane>
          </Tabs>
        </Modal>

        {/* VERIFICATION CODE MODAL */}
        <Modal
          title={t("register.verificationCodeTitle")}
          open={isCodeModalVisible}
          onOk={handleCodeModalOk}
          onCancel={handleCodeModalCancel}
          footer={[
            <Button
              key="back"
              onClick={handleCodeModalCancel}
              id="cancel-code-btn"
            >
              {t("register.cancel")}
            </Button>,
            <Button key="submit" onClick={handleCodeModalOk} id="submit-code-btn">
              {t("register.verifyCode")}
            </Button>,
          ]}
          className="enter-your-code-modal"
        >
          <label className="please-check-text">
            {t("register.verificationCodeText")}
          </label>
          <Input
            className="verification-code-input"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
          />
        </Modal>
      </div>
    </section>
  );
};

export default Register;
